import { createContext, useContext, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";

const AuthCtx = createContext(null);

/*
  Sign-in is Microsoft Entra ID SSO only, through Supabase Auth's built-in
  "azure" provider. There is no password, register, forgot-password or
  first-login reset flow any more — Microsoft (and the tenant's own MFA /
  Conditional Access) does all of that before Supabase ever sees the user.

  The provider is configured in Supabase (Authentication → Sign In / Providers
  → Azure) with the Indorama tenant URL, so only accounts in that tenant can
  get a session at all. See supabase/schema_sso_microsoft.sql for the
  database side and the setup checklist.

  An existing password-era account is kept, not duplicated: Supabase links the
  Microsoft identity onto the existing auth.users row with the same email, so
  its id — and with it public.users.role and everything the person owns —
  carries over. That only happens when the Microsoft token marks the email as
  verified (the `xms_edov` optional claim on the Entra app registration);
  without it Supabase would create a second, empty "member" account instead.

  A first-time person gets a public.users row with role "member" from the
  handle_new_user() trigger, same as self-registration used to.
*/

// Where to land after the round-trip to Microsoft. The OAuth redirect wipes
// React Router state, so the deep link ProtectedRoute remembered (e.g. a
// report link from an email) is parked here across the hop.
const POST_LOGIN_KEY = "postLoginRedirect";

const isSafePath = (p) => typeof p === "string" && /^\/(?!\/)/.test(p);

// Read during render (so it must not consume the value — StrictMode renders
// twice); clearPostLoginRedirect() is called from an effect once it's used.
export function peekPostLoginRedirect() {
  try {
    const p = sessionStorage.getItem(POST_LOGIN_KEY);
    return isSafePath(p) ? p : "/";
  } catch {
    return "/";
  }
}

export function clearPostLoginRedirect() {
  try { sessionStorage.removeItem(POST_LOGIN_KEY); } catch { /* ignore */ }
}

/*
  Mailboxes moving from @ng.indorama.com / @sn.indorama.com to @indorama.com.

  The person's app account still has the old address, Microsoft signs them in
  with the new one, and Supabase — unable to match the two — creates a fresh
  empty account. After a Microsoft-only sign-in the app asks the
  claim-legacy-account edge function whether that's what happened. If exactly
  one old account exists, it is merged automatically — no question (a
  contractor taken on full-time keeps their old data, 2026-10-09). The old
  account takes the new email (see
  supabase/schema_sso_legacy_account_merge.sql).

  "Nothing to import" is remembered per account per device in localStorage.
*/
const CLAIMING = Symbol("claiming");
// "2": an older "Start fresh" answer (from when the app asked first) must not
// stop the automatic merge now, so the old key is simply never read again.
const CLAIM_KEY = "legacyClaimChecked2:";

// A duplicate is always a Microsoft-only login (no password identity).
const mayBeDuplicate = (authUser) => {
  const ids = authUser?.identities || [];
  return ids.length > 0 && ids.every((i) => i.provider === "azure");
};

const checkedThisLoad = new Set(); // asked already in this page load
const claimAlreadyDecided = (userId) => {
  try { return localStorage.getItem(CLAIM_KEY + userId) === "1"; } catch { return false; }
};
const markClaimDecided = (userId) => {
  try { localStorage.setItem(CLAIM_KEY + userId, "1"); } catch { /* ignore */ }
};


export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);
  // A reason the last sign-in was refused after Microsoft accepted it
  // (currently: the account is disabled in the app). Shown on /login.
  const [authError, setAuthError] = useState("");
  const claimInFlight = useRef(false);
  // { legacyEmail } while the "import your old data?" question is open.
  const [importOffer, setImportOffer] = useState(null);

  // The raw Supabase Auth user object's `.role` is always the fixed string
  // "authenticated" — never the app's admin/manager/member role, which
  // lives in public.users instead. Every admin/manager check in the app
  // (TeamPage, SecuritySettingsPage, etc.) reads user.role expecting the
  // app role, so without this merge those checks are silently always
  // false. users_public is safe to read here (no secrets in it).
  const attachProfile = async (authUser) => {
    if (!authUser) return authUser;
    const { data: profile } = await supabase.from("users_public").select("*").eq("id", authUser.id).single();
    return profile ? { ...authUser, ...profile } : authUser;
  };

  const resolveUser = async (session) => {
    if (!session?.user) return false;
    // A Microsoft-only sign-in may be a fresh duplicate of the person's real
    // account (see the note above AuthProvider). Check once, outside this
    // callback (see checkLegacyAccount), holding the app on "Loading…"
    // meanwhile so an exact duplicate never flashes up as an empty account.
    if (mayBeDuplicate(session.user)) {
      if (claimInFlight.current) return CLAIMING;
      if (!checkedThisLoad.has(session.user.id) && !claimAlreadyDecided(session.user.id)) {
        checkedThisLoad.add(session.user.id);
        claimInFlight.current = true;
        setTimeout(() => checkLegacyAccount(session), 0);
        return CLAIMING;
      }
    }
    const u = await attachProfile(session.user);
    // Microsoft says who you are; whether you may use THIS app is still ours
    // to decide. A disabled account is turned away and its session dropped.
    // The sign-out is deferred because supabase-js deadlocks if an auth call
    // is awaited inside its own onAuthStateChange callback.
    if (u?.disabled) {
      setAuthError("This account has been disabled. Ask an administrator to re-enable it.");
      setTimeout(() => supabase.auth.signOut().catch(() => {}), 0);
      return false;
    }
    return u;
  };

  // Runs via setTimeout, never inside onAuthStateChange: functions.invoke()
  // reads the session, and supabase-js deadlocks if that happens inside its
  // own auth callback.
  const checkLegacyAccount = async (session) => {
    let data = null;
    try {
      ({ data } = await supabase.functions.invoke("claim-legacy-account", { body: { action: "check" } }));
    } catch { /* treat as nothing to import, but don't remember it — ask again next time */ }
    claimInFlight.current = false;
    if (data?.merged) {
      await signInAgainAfterMerge();
      return;
    }
    if (data?.offer) {
      // Not remembered yet: if they close the tab without answering, they're
      // asked again next sign-in.
      setImportOffer({ legacyEmail: data.legacy_email });
    } else if (data) {
      markClaimDecided(session.user.id);
    }
    setUser(await resolveUser(session));
    setReady(true);
  };

  // The current session belonged to the duplicate, which no longer exists.
  // Sign in again: Microsoft is already signed in, so it's an instant redirect
  // straight into the original account. The deep link (if any) is still
  // parked in sessionStorage, so it isn't passed again.
  const signInAgainAfterMerge = async () => {
    await supabase.auth.signOut({ scope: "local" }).catch(() => {});
    await loginWithMicrosoft(null).catch(() => { setUser(false); setReady(true); });
  };

  // "Import my old data" — throws the reason if the merge was refused (e.g.
  // this new account already has data of its own).
  const importLegacyAccount = async () => {
    const { data, error } = await supabase.functions.invoke("claim-legacy-account", { body: { action: "import" } });
    if (error || !data?.merged) throw new Error(await resolveFnError(error, data));
    setImportOffer(null);
    await signInAgainAfterMerge();
  };

  // "Start fresh" — keep this new account and stop asking on this device.
  const declineLegacyImport = () => {
    if (user?.id) markClaimDecided(user.id);
    setImportOffer(null);
  };

  // supabase.functions.invoke() puts a non-2xx response's real reason in
  // error.context, not error.message.
  const resolveFnError = async (error, data) => {
    if (data?.error) return String(data.error);
    if (error?.context) {
      try {
        const body = await error.context.clone().json();
        if (body?.error) return String(body.error);
      } catch { /* not JSON */ }
    }
    return error?.message || "Couldn't import your old account. Please try again.";
  };

  const refresh = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const u = await resolveUser(session);
      if (u === CLAIMING) return; // claimLegacyAccount sets user/ready when it finishes
      setUser(u);
      setReady(true);
    } catch {
      setUser(false);
      setReady(true);
    }
  };

  useEffect(() => {
    refresh();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const u = await resolveUser(session);
      if (u === CLAIMING) return;
      setUser(u);
      setReady(true);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Leaves the page: the browser goes to Microsoft and comes back to /login,
  // where supabase-js (detectSessionInUrl) picks the session out of the URL
  // and onAuthStateChange above takes it from there.
  const loginWithMicrosoft = async (nextPath) => {
    setAuthError("");
    try {
      if (isSafePath(nextPath)) sessionStorage.setItem(POST_LOGIN_KEY, nextPath);
    } catch { /* storage blocked — they just land on the Dashboard */ }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "azure",
      options: {
        scopes: "openid profile email",
        redirectTo: `${window.location.origin}/login`,
      },
    });
    if (error) throw error;
  };

  // Break-glass email + password sign-in for accounts that aren't Microsoft
  // accounts (e.g. admin@indorama.com). Offered only as a small link on the
  // login page. onAuthStateChange picks the session up from here.
  const loginWithPassword = async (email, password) => {
    setAuthError("");
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    if (error) throw error;
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.warn("AuthContext.logout: request failed, clearing client state anyway", e);
    }
    setImportOffer(null);
    setUser(false);
  };

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (detail.message) return detail.message;
    return String(detail);
  };

  return (
    <AuthCtx.Provider value={{ user, ready, authError, loginWithMicrosoft, loginWithPassword, logout, refresh, formatApiErrorDetail, importOffer, importLegacyAccount, declineLegacyImport }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
